import React, { useCallback, useEffect, useRef, useState } from 'react';
import { type BlockerFunction, useBlocker } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@eluve/components';
import { useBeforeUnload } from '@eluve/utility-hooks';

/**
 * Guards the template editor against silently discarding unsaved work.
 * Intercepts both in-app navigation (React Router `useBlocker`) and
 * browser-level unload (tab close / reload) while the editor is dirty,
 * prompting the user to confirm before leaving.
 *
 * Returns `markDirty`/`markClean` to flag editing and to clear the flag
 * after a successful save, plus the confirmation `dialog` to render.
 * Dirtiness is held in a ref so a save handler can `markClean()`
 * synchronously right before a programmatic `navigate()` without the
 * blocker catching its own post-save navigation.
 *
 * `enabled` lets callers turn the guard off entirely (e.g. read-only
 * templates, whose tweaks are throwaway exploration).
 */
export const useUnsavedChangesGuard = (enabled = true) => {
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);

  const setDirty = useCallback((value: boolean) => {
    isDirtyRef.current = value;
    setIsDirty(value);
  }, []);

  useBeforeUnload(enabled && isDirty);

  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        enabled &&
        isDirtyRef.current &&
        // Guard any real navigation away from this location, including ones
        // that only change search/hash (those can still refetch or remount
        // editor state) — not just pathname changes.
        (currentLocation.pathname !== nextLocation.pathname ||
          currentLocation.search !== nextLocation.search ||
          currentLocation.hash !== nextLocation.hash),
      [enabled],
    ),
  );

  // If the guard stops applying (e.g. saved, or disabled) while a prompt
  // is open, drop it rather than stranding the navigation.
  useEffect(() => {
    if (blocker.state === 'blocked' && !(enabled && isDirtyRef.current)) {
      blocker.reset();
    }
  }, [enabled, blocker]);

  const dialog = (
    <AlertDialog
      open={blocker.state === 'blocked'}
      onOpenChange={(open) => {
        if (!open && blocker.state === 'blocked') {
          blocker.reset();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes to this template. If you leave now, they’ll
            be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => {
              if (blocker.state === 'blocked') {
                blocker.reset();
              }
            }}
          >
            Keep editing
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (blocker.state === 'blocked') {
                blocker.proceed();
              }
            }}
          >
            Discard changes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return {
    markDirty: useCallback(() => setDirty(true), [setDirty]),
    markClean: useCallback(() => setDirty(false), [setDirty]),
    dialog,
  };
};
